import React, { useState } from 'react';
import { useQuery } from 'react-query';

import { Typography } from 'antd';
import styled from 'styled-components';

import { getShopRequestShopperEmails } from 'apis/ShopRequestAPI';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';

import ManageShopRequestShopperEmailsModal from './ManageShopRequestShopperEmailsModal';
import { MAILBOX_CONNECTION_STATUS, MAILBOX_CONNECTION_STATUS_LABELS } from '../../constants';
import { ShopRequestShopperEmailType } from '../../types';
import { useEditShopRequestModalContext } from '../EditShopRequestModalContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 22px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 4px;
  border-bottom: 1px solid var(--line-gray);
`;

const Title = styled(Typography.Text)`
  font-family: var(--font-bold);
  font-size: 14px;
  color: var(--text-primary);
`;

const EditButton = styled(Button)`
  color: var(--dark-blue);
  cursor: pointer;
  font-size: 12px;
  padding: 0;
  box-shadow: none;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ShopperEmailsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 140px;
  overflow-y: auto;
  padding-right: 6px;
`;

const ShopperEmailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  justify-content: space-between;
`;

const ShopperEmailDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const ShopperEmailPrimary = styled(Typography.Text)`
  font-size: 13px;
  font-family: var(--font-bold);
  line-height: 1.35;
  color: var(--text-primary);
  word-break: break-word;
`;

const ShopperEmailSecondary = styled(Typography.Text)`
  font-size: 13px;
  line-height: 1.35;
  color: var(--text-secondary);
  word-break: break-word;
`;

const StatusBadge = styled.span<{ status?: string | null }>`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 1px 8px;
  white-space: nowrap;
  border: 1px solid
    ${({ status }) => {
      if (status === MAILBOX_CONNECTION_STATUS.ACTIVE) {
        return 'var(--positive-green)';
      }

      if (status === MAILBOX_CONNECTION_STATUS.NEEDS_RECONNECT) {
        return 'var(--negative-orange)';
      }

      if (status === MAILBOX_CONNECTION_STATUS.REVOKED || status === MAILBOX_CONNECTION_STATUS.ERROR) {
        return 'var(--error-red)';
      }

      return 'var(--line-gray)';
    }};
  border-radius: 999px;
  font-size: 11px;
  color: ${({ status }) => {
    if (status === MAILBOX_CONNECTION_STATUS.ACTIVE) {
      return 'var(--positive-green)';
    }

    if (status === MAILBOX_CONNECTION_STATUS.NEEDS_RECONNECT) {
      return 'var(--negative-orange)';
    }

    if (status === MAILBOX_CONNECTION_STATUS.REVOKED || status === MAILBOX_CONNECTION_STATUS.ERROR) {
      return 'var(--error-red)';
    }

    return 'var(--text-secondary)';
  }};
`;

const EmptyState = styled(Typography.Text)`
  font-size: 13px;
  color: var(--text-secondary);
`;

const getShopperEmailDisplay = (shopperEmail: ShopRequestShopperEmailType) => {
  if (shopperEmail.label) {
    return {
      primary: shopperEmail.name ? `${shopperEmail.label} (${shopperEmail.name})` : shopperEmail.label,
      secondary: shopperEmail.email || undefined,
    };
  }

  if (shopperEmail.name) {
    return {
      primary: shopperEmail.name,
      secondary: shopperEmail.email || undefined,
    };
  }

  return { primary: shopperEmail.email || shopperEmail.phone || 'Shopper contact' };
};

const EditShopRequestModalShopperEmails = () => {
  const { showMessage } = useRootContext();
  const { shopRequest } = useEditShopRequestModalContext();

  const [manageEmailsOpened, setManageEmailsOpened] = useState(false);
  const shopRequestId = shopRequest?.id;

  const {
    data: shopperEmails = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery<ShopRequestShopperEmailType[]>({
    queryKey: ['shop-request', shopRequestId, 'shopper-emails'],
    queryFn: () => getShopRequestShopperEmails(shopRequestId as number),
    enabled: Boolean(shopRequestId),
    refetchOnWindowFocus: false,
    onError: () => {
      showMessage('error', 'Failed to fetch shopper contacts.');
    },
  });

  if (!shopRequest) {
    return null;
  }

  return (
    <Container>
      <Header>
        <Title>Shopper Contacts</Title>
        <HeaderActions>
          <EditButton size="small" type="link" onClick={() => setManageEmailsOpened(true)}>
            Edit
          </EditButton>
        </HeaderActions>
      </Header>

      {(isLoading || isFetching) && <EmptyState>Loading shopper contacts...</EmptyState>}
      {!isLoading && !isFetching && shopperEmails.length === 0 && (
        <EmptyState>No shopper contacts have been linked to this request yet.</EmptyState>
      )}
      {!isLoading && !isFetching && shopperEmails.length > 0 && (
        <ShopperEmailsList className="mini-scroll">
          {shopperEmails.map((shopperEmail) => {
            const { primary, secondary } = getShopperEmailDisplay(shopperEmail);

            return (
              <ShopperEmailItem key={shopperEmail.id ?? shopperEmail.email}>
                <ShopperEmailDetails>
                  <ShopperEmailPrimary>{primary}</ShopperEmailPrimary>
                  {secondary && <ShopperEmailSecondary>{secondary}</ShopperEmailSecondary>}
                  {shopperEmail.phone && <ShopperEmailSecondary>{shopperEmail.phone}</ShopperEmailSecondary>}
                </ShopperEmailDetails>
                <StatusBadge status={shopperEmail.mailbox_connection_status}>
                  {MAILBOX_CONNECTION_STATUS_LABELS[shopperEmail.mailbox_connection_status || 'default'] ??
                    MAILBOX_CONNECTION_STATUS_LABELS.default}
                </StatusBadge>
              </ShopperEmailItem>
            );
          })}
        </ShopperEmailsList>
      )}

      <ManageShopRequestShopperEmailsModal
        openModal={manageEmailsOpened}
        shopRequest={shopRequest}
        shopperEmails={shopperEmails}
        isFetchingShopperEmails={isFetching}
        isLoadingShopperEmails={isLoading}
        refetchShopperEmails={refetch}
        onClose={() => {
          setManageEmailsOpened(false);
          refetch();
        }}
      />
    </Container>
  );
};

export default EditShopRequestModalShopperEmails;
